/**
 * `session-end.ts` — what the panel does when the admin session is over.
 *
 * ══ WHY (issue #2936, measured 2026-09-21) ═════════════════════════════════
 *
 * The admin app client has a fixed `refreshTokenValidity` ON PURPOSE
 * (auth-stack.ts): 8 hours from audit P2-11, 24 hours since the owner's
 * decision of 2026-09-29 (the NIST SP 800-63B-4 AAL2 limit). A Cognito refresh
 * token is not extended by use, so EVERY admin session ends that long after
 * sign-in, however active the operator is. The panel then dropped to `/login` with no sentence. It was
 * logged five times in two days as "the panel signs itself out", and explained
 * wrongly twice (a deploy; idle expiry) before the setting was read.
 *
 * The setting is a security control and stays. What was wrong is the
 * experience, and three things fix it:
 *   1. the login page SAYS why it is showing;
 *   2. after sign-in the operator returns to the page they were on;
 *   3. a 403 no longer signs anyone out. 403 means "you may not see this" (a
 *      missing permission, a WAF block) — the session is fine. Only a 401 means
 *      the session is over.
 *
 * PURE: no `window`, no storage — so the rules are testable.
 */

export const SESSION_ENDED_REASON = 'session_ended';

export const SESSION_ENDED_MESSAGE =
  'Your admin session ended — for security, a session lasts 24 hours. Sign in again and you ' +
  'will return to the page you were on.';

/**
 * A path this app may send the browser to after sign-in, or `null`.
 *
 * Internal only: it must start with ONE slash (so `//evil.example` and
 * `https://…` are refused), must not be the login page itself (a loop), and
 * must carry no control character or backslash a browser could normalise into
 * another origin.
 */
export function safeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 512) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f]/.test(raw)) return null;
  if (raw === '/login' || raw.startsWith('/login?') || raw.startsWith('/login/')) return null;
  return raw;
}

/**
 * Where to send a browser that has no session, remembering where it was.
 *
 * `hadSession` decides whether the login page may SAY "your session ended". A
 * person who never signed in and opens a panel URL has lost nothing, and telling
 * them otherwise would be untrue. The evidence is the `admin_email` marker:
 * sign-in writes it, an EXPLICIT sign-out removes it, an expiry leaves it.
 */
export function sessionEndedLoginPath(pathname: string, search: string, hadSession: boolean): string {
  const back = safeReturnPath(`${pathname}${search}`);
  const params = new URLSearchParams();
  if (hadSession) params.set('reason', SESSION_ENDED_REASON);
  if (back !== null) params.set('to', back);
  const query = params.toString();
  return query.length > 0 ? `/login?${query}` : '/login';
}

/** What the login page reads from its own URL. */
export function readLoginContext(search: string): { sessionEnded: boolean; returnTo: string | null } {
  const params = new URLSearchParams(search);
  return {
    sessionEnded: params.get('reason') === SESSION_ENDED_REASON,
    returnTo: safeReturnPath(params.get('to')),
  };
}

/**
 * Does this API error mean the SESSION is over?
 *
 * 401 only. Until 2026-09-21 a 403 signed the operator out too: an admin who
 * opened a page their role does not include was thrown to the login screen.
 */
export function errorEndsTheSession(error: { status?: unknown } | null | undefined): boolean {
  return error?.status === 401;
}

// ═══════════════════════════════════════════════════════════════════════════
// SIGN-OUT ENDS THE SESSION, AND NO OTHER IDENTITY STAYS BEHIND (#2936)
// ═══════════════════════════════════════════════════════════════════════════
//
// Until 2026-09-30 sign-out called `cognitoUser.signOut()` with no argument. In
// amazon-cognito-identity-js 6.x that is the LOCAL sign-out: it deletes this
// browser's keys and revokes nothing, so a copied refresh token stayed usable for
// the rest of its life (24 hours since #3220). And a sign-in never removed a
// previous identity's keys, so on a shared workstation admin A's refresh token
// stayed in the browser after admin B signed in. The owner chose the fix on
// 2026-09-30, and chose NO inactivity timeout (the 24-hour cap from sign-in stays
// the only limit).

/** Every key amazon-cognito-identity-js writes for this app client starts so. */
const cognitoKeyPrefix = (clientId: string): string => `CognitoIdentityServiceProvider.${clientId}.`;

/**
 * The localStorage keys of this app client that belong to an identity OTHER than
 * `currentUser` (all identities when it is null). The library writes
 * `CognitoIdentityServiceProvider.<clientId>.<username>.<field>` and one
 * `<clientId>.LastAuthUser`. The admin pool's username is an EMAIL, which has
 * dots, so the field is what follows the LAST dot. `LastAuthUser` and other
 * clients' keys are never returned. PURE.
 */
export function otherIdentityKeys(keys: readonly string[], clientId: string, currentUser: string | null): string[] {
  const prefix = cognitoKeyPrefix(clientId);
  return keys.filter((key) => {
    if (!key.startsWith(prefix)) return false;
    const rest = key.slice(prefix.length);
    const dot = rest.lastIndexOf('.');
    if (dot <= 0) return false; // `LastAuthUser` (no user part), or malformed
    return rest.slice(0, dot) !== currentUser;
  });
}

/** The one method of a CognitoUser the sign-out needs; a structural type keeps this module pure. */
export interface RevocableSession {
  /** With a callback the library revokes the refresh token first (RevokeToken), then clears the browser. */
  signOut(callback?: (error?: unknown) => void): void;
}

/** A revocation that does not answer in this long never holds the operator on the page. */
export const REVOKE_TIMEOUT_MS = 5000;

/**
 * Sign-out that ENDS the session: the refresh token is revoked at Cognito
 * (RevokeToken; the admin client has token revocation on), then this browser is
 * cleared. The library's revoking path does NOT clear the browser when it cannot
 * start (the session already expired: `getSession` fails and the callback gets the
 * error), so on any error, and on a timeout, this clears it with the local
 * sign-out. Resolves `'revoked'` or `'cleared_locally'`; it never rejects, so
 * sign-out can never fail for the operator.
 */
export function endCognitoSession(
  session: RevocableSession,
  timeoutMs: number = REVOKE_TIMEOUT_MS,
): Promise<'revoked' | 'cleared_locally'> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (revoked: boolean) => {
      if (settled) return;
      settled = true;
      if (!revoked) session.signOut(); // local sign-out: always leaves this browser clean
      resolve(revoked ? 'revoked' : 'cleared_locally');
    };
    const timer = setTimeout(() => settle(false), timeoutMs);
    try {
      session.signOut((error?: unknown) => {
        clearTimeout(timer);
        settle(error === undefined || error === null);
      });
    } catch {
      clearTimeout(timer);
      settle(false);
    }
  });
}
